import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Crown, AlertCircle } from "lucide-react";
import { LoadingModal } from "@/components/ui/LoadingModal";
import { useInvalidateUserInfo, useUserInfo } from "@/hooks/useUserInfo";
import PageTitleScript from "@/components/MetaScript";
import { Button } from "@/components/ui/button";

export function SettingsSubscriptionSuccessPage() {
  const contactUrl = import.meta.env.VITE_MAIN_SITE + "/contact";
  const navigate = useNavigate();
  const invalidateUserInfo = useInvalidateUserInfo();
  const { data: userInfo, isLoading: userInfoLoading, isError } = useUserInfo();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<
    "loading" | "success" | "failed" | "error"
  >("loading");
  const [hasCheckedInitially, setHasCheckedInitially] = useState(false);

  useEffect(() => {
    // Initial data fetch on mount
    invalidateUserInfo();

    // Initial check with delay
    const initialTimeout = setTimeout(() => {
      setIsInitialLoading(false);
      setHasCheckedInitially(true);
    }, 1500);

    return () => clearTimeout(initialTimeout);
  }, []); // No dependencies to prevent re-running

  // Separate effect to handle subscription status changes
  useEffect(() => {
    if (hasCheckedInitially && userInfo && !userInfoLoading && !isError) {
      if (userInfo.subscription?.is_subscribed) {
        setSubscriptionStatus("success");
        // Auto-redirect after success
        setTimeout(() => {
          navigate("/settings/subscription", { replace: true });
        }, 3000);
      } else if (subscriptionStatus === "loading") {
        // Only set to failed if we're coming from loading state
        setSubscriptionStatus("failed");
      }
    } else if (
      hasCheckedInitially &&
      (isError || !userInfo) &&
      subscriptionStatus === "loading"
    ) {
      setSubscriptionStatus("error");
    }
  }, [userInfo, hasCheckedInitially, isError, userInfoLoading]); // Removed subscriptionStatus from deps

  // Polling effect - only runs when status is specifically "failed"
  useEffect(() => {
    if (subscriptionStatus === "failed") {
      const pollInterval = setInterval(() => {
        invalidateUserInfo();
      }, 5000);

      return () => clearInterval(pollInterval);
    }
  }, [subscriptionStatus]); // Removed invalidateUserInfo from deps

  const handleRetry = () => {
    navigate("/settings/subscription", { replace: true });
  };

  // Show loading while checking subscription status
  if (isInitialLoading || subscriptionStatus === "loading") {
    return (
      <LoadingModal isOpen={true} message="Confirming your subscription..." />
    );
  }

  // Success state - subscription is active
  if (subscriptionStatus === "success") {
    return (
      <div className="bg-gradient-to-r from-gray-900 to-black rounded-xl text-white shadow-lg min-h-screen relative flex items-center justify-center">
        <PageTitleScript title="Subscription Successful" />

        <div className="text-center px-8 max-w-md">
          {/* Success Icon */}
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-tx-success rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent-bg-warm rounded-full flex items-center justify-center">
              <Crown className="w-5 h-5 text-tx-dark" />
            </div>
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-white mb-4">
            Welcome to PRO!
          </h1>

          <p className="text-gray-300 text-lg mb-8 leading-relaxed">
            Your subscription has been activated successfully. You now have
            access to all PRO features.
          </p>

          {/* Loading indicator */}
          <div className="flex items-center justify-center gap-2 text-gray-400">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
            <div
              className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"
              style={{ animationDelay: "0.2s" }}
            ></div>
            <div
              className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"
              style={{ animationDelay: "0.4s" }}
            ></div>
            <span className="text-sm ml-2">Redirecting to your account...</span>
          </div>
        </div>
      </div>
    );
  }

  // Failed state - subscription didn't activate
  if (subscriptionStatus === "failed") {
    return (
      <div className="bg-white min-h-screen relative flex items-center justify-center">
        <PageTitleScript title="Subscription Failed" />

        <div className="text-center px-8 py-12 max-w-md">
          {/* Error Icon */}
          <div className="w-24 h-24 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-8">
            <AlertCircle className="w-12 h-12 text-white" />
          </div>

          <p className="text-gray-600 text-base mb-6 leading-6">
            We couldn't confirm your subscription activation. If you believe
            this is an error, please{" "}
            <a href={contactUrl} className="underline underline-offset-2">
              contact support
            </a>
            .
          </p>

          <div className=" border border-blue-200 rounded-xl p-5 mb-8">
            <div className="flex items-center justify-center gap-1 text-blue-400">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <div
                className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"
                style={{ animationDelay: "0.2s" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"
                style={{ animationDelay: "0.4s" }}
              ></div>
              <span className="text-sm ml-2">Checking for updates...</span>
            </div>
          </div>

          <div className="space-y-4 mb-10">
            <Button
              onClick={handleRetry}
              variant="link"
              size="lg"
              className="w-full"
            >
              Go to Subscription Settings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error state - couldn't load user info
  return (
    <div className="bg-white min-h-screen relative flex items-center justify-center">
      <PageTitleScript title="Error" />

      <div className="text-center px-8 py-12 max-w-md">
        {/* Error Icon */}
        <div className="w-24 h-24 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-8">
          <AlertCircle className="w-12 h-12 text-white" />
        </div>

        {/* Error Message */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Something went wrong
        </h1>

        <p className="text-gray-600 text-lg mb-8 leading-relaxed">
          We couldn't load your account information. Please try again.
        </p>

        <Button
          onClick={handleRetry}
          variant="primary"
          size="lg"
          className="w-full"
        >
          Try Again
        </Button>
      </div>
    </div>
  );
}
